import { forwardRef, type ComponentType, type PropsWithChildren } from "react"

import { t } from "@be-miniapp/i18n"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { BePickupIcon } from "@be-miniapp/ui/components/ui/icons/be-pickup"
import { CloseIcon } from "@be-miniapp/ui/components/ui/icons/close"
import { PenIcon } from "@be-miniapp/ui/components/ui/icons/pen"
import { Input } from "@be-miniapp/ui/components/ui/input"

import type { ConfirmAddressPaneModelReturn } from "./use-confirm-address-pane-model"

export type ConfirmAddressSheetProps = PropsWithChildren<{
  visible: boolean
  mask?: boolean
  maskClosable?: boolean
  autoHeight?: boolean
  onClose: () => void
}>

export interface ConfirmAddressPaneViewProps {
  model: ConfirmAddressPaneModelReturn
  Sheet: ComponentType<ConfirmAddressSheetProps>
  /** e.g. AppView `mb-safe-bottom` vs Zalo `mb-safe-bottom-za` */
  confirmButtonClassName: string
}

export const ConfirmAddressPaneView = forwardRef<
  HTMLDivElement,
  ConfirmAddressPaneViewProps
>(function ConfirmAddressPaneView(
  { model, Sheet, confirmButtonClassName },
  ref
) {
  const {
    note,
    tempNote,
    setTempNote,
    isNoteOpen,
    onNoteClose,
    openNoteSheet,
    onConfirm,
    pointData,
    inputNoteRef,
    onSaveNote,
    goEditPickup,
    isLoading,
  } = model

  return (
    <div
      ref={ref}
      className="absolute inset-x-0 bottom-0 flex flex-col justify-between bg-white p-5 shadow-pane"
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-0.5">
          <BePickupIcon className="scale-[0.7]" />
          <span className="text-gray-600 text-xs">
            {t("ride_current_pickup")}
          </span>
        </div>
        <div
          className="mb-4 flex flex-1 items-center justify-between"
          onClick={goEditPickup}
        >
          <div className="flex h-full flex-col">
            <span className="mb-1 line-clamp-2 font-bold text-[0.9rem]">
              {pointData?.name || t("home.input_dropoff_loading")}
            </span>
            <span className="line-clamp-1 font-medium text-[0.78rem] text-gray-500">
              {pointData?.address?.startsWith(pointData?.name ?? "")
                ? (pointData.address ?? "").split(", ").slice(1).join(", ")
                : pointData?.address}
            </span>
          </div>

          <Button variant="ghost" className="pr-0 pl-4">
            <PenIcon name="pen" className="size-5" />
          </Button>
        </div>

        <div
          onClick={openNoteSheet}
          className="mb-2 rounded-md border border-gray-300 border-dashed px-2"
        >
          <Button
            variant="ghost"
            size="auto"
            className="p-2 px-1 font-medium text-blue-700 text-xs placeholder:text-blue-600"
          >
            <div className="flex items-center gap-1">
              <img src="/icons/note.svg" className="size-4" alt="" />
              {note ? note : t("ride.confirm_address.placeholder.note")}
            </div>
          </Button>
        </div>
      </div>

      <Button
        className={`fold-bold mt-2 cursor-pointer text-bold shadow-md ${confirmButtonClassName}`}
        onClick={onConfirm}
        disabled={isLoading}
      >
        {t("ride.confirm_address.actions.confirm")}
      </Button>

      {isNoteOpen ? (
        <Sheet
          visible={isNoteOpen}
          mask
          maskClosable
          autoHeight
          onClose={onNoteClose}
        >
          <div className="flex w-full flex-col divide-y divide-gray-100 overflow-hidden rounded-t-lg bg-white">
            <div className="flex items-center justify-between px-4 py-3 font-semibold">
              <span>{t("ride_confirmaddress_notefordriver_input_title")}</span>
              <CloseIcon width={20} height={20} onClick={onNoteClose} />
            </div>
            <div className="flex flex-col gap-3 px-4 py-6">
              <Input
                className="rounded-none border-blue-600 border-b-2"
                value={tempNote}
                id="note-input"
                autoFocus
                ref={inputNoteRef}
                onBlur={() => inputNoteRef.current?.focus()}
                onChange={(e) => setTempNote(e.target.value)}
              />

              <Button onClick={onSaveNote}>{t("common.save")}</Button>
            </div>
          </div>
        </Sheet>
      ) : null}
    </div>
  )
})

ConfirmAddressPaneView.displayName = "ConfirmAddressPaneView"
